import { derivedId, resolveUnit, stockLineName, stockPreview, unitByCode, type StockConsumeV1, type StockRefuelV1, type StockReturnV1 } from '@gk/contracts'
import type { GkDb, OutboxRow, OverlayRow } from './db.ts'
import type { Projector } from './projectors.ts'
import { localDate } from '../lib/time.ts'

/** M15-A (docs/M15.md rules 1, 2, 5): the projections of the three stock FACTS, so a consumption typed at the finish, a fill-up at the pump and a return show on
 *  this device at once, pending, until the server's rows replace them. The ids are the server's: a consumption line is v5(work_session_id, 'stock:<line_no>')
 *  (a retried finish never consumes twice), a fill-up and a return carry the device's own id. qty_base is the twin's preview under the product's newest
 *  version on this device (the server converts again and its value is the truth); a line the twin refuses is not projected (the server's refusal then tells the
 *  person); the place is the one named, else the site's default (a fill-up: the site's first tank, else the default), as the server picks it. Registered in
 *  projectors.ts under the M15a hook. */
interface ProductLite { id: string; base_unit_code: string; archived_at?: string | null }
interface VersionLite { id: string; product_id: string; version_no: number; density_kg_per_l?: number | string | null }
interface LocationLite { id: string; at_site_id: string; kind: string; is_default: boolean; archived_at?: string | null }

async function placeOf(db: GkDb, siteId: string, named: string | null | undefined, tankFirst: boolean): Promise<string | null> {
  if (named) return named
  const rows = (await db.stock_location.toArray()) as unknown as LocationLite[]
  const live = rows.filter((l) => l.at_site_id === siteId && !l.archived_at)
  return (tankFirst ? live.find((l) => l.kind === 'tank') : undefined)?.id ?? live.find((l) => l.is_default)?.id ?? null
}
async function converted(db: GkDb, productId: string, quantity: number, unit: string): Promise<{ versionId: string | null; unitCode: string; qtyBase: number; baseUnit: string } | null> {
  const product = (await db.product.get(productId)) as unknown as ProductLite | undefined
  if (!product) return null
  const versions = (await db.product_version.where('product_id').equals(productId).toArray()) as unknown as VersionLite[]
  const newest = versions.sort((a, b) => b.version_no - a.version_no)[0]
  let unitCode: string
  try { unitCode = resolveUnit(unit) } catch { return null }
  const density = newest?.density_kg_per_l
  const preview = stockPreview(quantity, unitCode, product.base_unit_code, density === null || density === undefined || density === '' ? null : density, unitByCode(unitCode)?.dimension)
  if (!preview.ok) return null
  return { versionId: newest?.id ?? null, unitCode, qtyBase: Number(preview.qtyBase), baseUnit: product.base_unit_code }
}
const row = (cmd: OutboxRow, id: string, fields: Record<string, unknown>): OverlayRow =>
  ({ key: `stock.movement:${id}`, table: 'stock.movement', id, command_id: cmd.command_id, op: 'insert', row: { id, org_id: cmd.org_id, site_id: null, batch_id: null, job_id: null, work_session_id: null, work_order_id: null, asset_id: null, reversal_of: null, line_no: 1, note: null, actor_person_id: cmd.actor_person_id ?? null, ...fields, _pending: true } })

export const stockConsume: Projector = async (cmd, db) => {
  const p = cmd.payload as StockConsumeV1
  const tz = ((await db.site.get(cmd.site_id)) as { timezone?: string } | undefined)?.timezone ?? 'Europe/Paris'
  const session = ((await db.work_session.get(p.work_session_id)) ?? (await db.overlay.get(`ops.work_session:${p.work_session_id}`))?.row) as { job_id?: string | null } | undefined
  const location = await placeOf(db, cmd.site_id, p.location_id, false)
  const out: OverlayRow[] = []
  for (const line of p.lines) {
    const c = await converted(db, line.product_id, line.quantity, line.unit)
    if (!c) continue
    out.push(row(cmd, derivedId(p.work_session_id, stockLineName(line.line_no)), {
      product_id: line.product_id, product_version_id: c.versionId, batch_id: line.batch_id ?? null, location_id: location, reason: 'consumption',
      qty_entered: line.quantity, unit_entered: c.unitCode, qty_base: -c.qtyBase, base_unit_code: c.baseUnit, job_id: session?.job_id ?? null, work_session_id: p.work_session_id,
      line_no: line.line_no, note: line.note?.trim() || null, occurred_at: cmd.occurred_at, occurred_local_date: localDate(cmd.occurred_at, tz), entered_at: cmd.occurred_at,
    }))
  }
  return out
}

export const stockRefuel: Projector = async (cmd, db) => {
  const p = cmd.payload as StockRefuelV1
  const tz = ((await db.site.get(cmd.site_id)) as { timezone?: string } | undefined)?.timezone ?? 'Europe/Paris'
  const c = await converted(db, p.product_id, p.quantity, p.unit)
  if (!c) return []
  const at = p.occurred_at ?? cmd.occurred_at
  return [row(cmd, p.movement_id, {
    product_id: p.product_id, product_version_id: c.versionId, location_id: await placeOf(db, cmd.site_id, p.location_id, true), reason: 'refuel',
    qty_entered: p.quantity, unit_entered: c.unitCode, qty_base: -c.qtyBase, base_unit_code: c.baseUnit, asset_id: p.asset_id,
    note: p.note?.trim() || null, occurred_at: at, occurred_local_date: localDate(at, tz), entered_at: cmd.occurred_at,
  })]
}

export const stockReturn: Projector = async (cmd, db) => {
  const p = cmd.payload as StockReturnV1
  const tz = ((await db.site.get(cmd.site_id)) as { timezone?: string } | undefined)?.timezone ?? 'Europe/Paris'
  const c = await converted(db, p.product_id, p.quantity, p.unit)
  if (!c) return []
  const at = p.occurred_at ?? cmd.occurred_at
  return [row(cmd, p.movement_id, {
    product_id: p.product_id, product_version_id: c.versionId, batch_id: p.batch_id ?? null, location_id: await placeOf(db, cmd.site_id, p.location_id, false), reason: 'return',
    qty_entered: p.quantity, unit_entered: c.unitCode, qty_base: c.qtyBase, base_unit_code: c.baseUnit, job_id: p.job_id ?? null,
    note: p.note?.trim() || null, occurred_at: at, occurred_local_date: localDate(at, tz), entered_at: cmd.occurred_at,
  })]
}
